import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { getSession } from '@website/services/session.service';
import { getCookieDomainFromHostname, getCookieName } from '@website/utils/cookies';
import { getLocaleCookieClient, setLocaleCookieClient } from '@website/utils/localeCookie';

/**
 * User type for cookie-based instant UI rendering.
 * - 'public': Not authenticated
 * - 'learner': Authenticated, regular user
 * - 'professional': Authenticated, professional user
 */
export type UserType = 'public' | 'learner' | 'professional';

/**
 * Get the cookie domain for client-side cookie operations.
 * Uses the shared getCookieDomainFromHostname to ensure consistency with server-side.
 */
function getClientCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return getCookieDomainFromHostname(window.location.hostname);
}

/**
 * Syncs the user_type cookie with the user's professional status.
 * This cookie is used by middleware and client components for instant rendering.
 * Exported for use in useUpdateUser to synchronously update cookie before navigation.
 *
 * IMPORTANT: Domain must match server-side cookie settings to avoid duplicate cookies.
 */
export function syncUserTypeCookie(isProfessional: boolean | undefined) {
  if (typeof window === 'undefined') return;

  if (isProfessional !== undefined) {
    const userType: UserType = isProfessional ? 'professional' : 'learner';
    const isProduction = process.env.NODE_ENV === 'production';
    const secureFlag = isProduction ? '; Secure' : '';
    const domain = getClientCookieDomain();
    const domainFlag = domain ? `; Domain=${domain}` : '';
    const cookieName = getCookieName('user_type');
    document.cookie = `${cookieName}=${userType}; path=/; max-age=31536000; SameSite=Lax${secureFlag}${domainFlag}`;
  }
}

/**
 * Clears the user_type cookie (sets it to expire immediately).
 * Called when session expires, user logs out, or authentication errors occur.
 *
 * IMPORTANT: Domain must match how cookie was set to properly clear it.
 */
function clearUserTypeCookie() {
  if (typeof window === 'undefined') return;
  const isProduction = process.env.NODE_ENV === 'production';
  const secureFlag = isProduction ? '; Secure' : '';
  const domain = getClientCookieDomain();
  const domainFlag = domain ? `; Domain=${domain}` : '';
  const cookieName = getCookieName('user_type');
  document.cookie = `${cookieName}=; path=/; max-age=0; SameSite=Lax${secureFlag}${domainFlag}`;
}

/**
 * Gets the user type from the cookie synchronously.
 * Safe to use in useState initializer for zero-flash rendering.
 * Returns 'public' if cookie is not set (not authenticated) or during SSR.
 */
function getUserTypeFromCookie(): UserType {
  if (typeof document === 'undefined') return 'public';

  const cookieName = getCookieName('user_type');
  const match = document.cookie.match(new RegExp(`(^| )${cookieName}=([^;]+)`));
  const value = match?.[2];

  if (value === 'professional' || value === 'learner') {
    return value;
  }
  return 'public';
}

/**
 * Query key for user type - used by both useUserType and useCurrentUser
 * to share state via React Query cache.
 */
export const USER_TYPE_QUERY_KEY = ['userType'] as const;

/**
 * Hook to get user type from cookie instantly without API call.
 * Uses React Query cache for state management, allowing useCurrentUser
 * to update the value when API returns.
 *
 * The `initialData` option ensures the cookie is read synchronously on first render,
 * avoiding the flash of wrong content that would occur with async queryFn.
 *
 * @returns UserType - 'public' | 'learner' | 'professional'
 */
export function useUserType(): UserType {
  const { data } = useQuery({
    queryKey: USER_TYPE_QUERY_KEY,
    queryFn: getUserTypeFromCookie,
    initialData: getUserTypeFromCookie, // Read cookie synchronously on first render
    staleTime: Infinity, // Never refetch automatically - useCurrentUser manages updates
    gcTime: Infinity, // Keep in cache forever
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  return data;
}

export function useCurrentUser() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const session = await getSession();
      // Explicitly return null instead of undefined for proper type handling
      return session?.user ?? null;
    }
  });

  // Auto-sync user_type cookie AND React Query cache whenever user data changes
  useEffect(() => {
    if (query.data) {
      const userType: UserType = query.data.isProfessional ? 'professional' : 'learner';
      syncUserTypeCookie(query.data.isProfessional);
      // Update React Query cache so useUserType gets the new value instantly
      queryClient.setQueryData(USER_TYPE_QUERY_KEY, userType);

      // Sync user's locale preference from backend to localStorage/cookie
      // This ensures the user's saved locale preference is applied when:
      // - User logs in on a new browser/device
      // - User's localStorage/cookie was cleared
      // - User returns after cookie expired
      if (query.data.locale) {
        const storedLocale = getLocaleCookieClient();
        // Only sync if there's no stored locale (first visit on this browser)
        // or if stored locale differs from user's backend preference
        if (!storedLocale || storedLocale !== query.data.locale) {
          setLocaleCookieClient(query.data.locale);
          console.log('[useCurrentUser] Synced user locale from backend:', query.data.locale);
        }
      }
    } else if ((query.data === null || query.isError) && !query.isLoading) {
      // Session expired, user logged out, or error occurred - clear cookie and cache
      clearUserTypeCookie();
      queryClient.setQueryData(USER_TYPE_QUERY_KEY, 'public');
    }
  }, [query.data, query.isLoading, query.isError, queryClient]);

  return query;
}

export function useCurrentUserSuspense() {
  return useSuspenseQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const session = await getSession();
      if (!session?.user) {
        throw new Error('User not found');
      }
      return session.user;
    }
  });
}
